import type { ReactNode, CSSProperties } from "react";
import { bgClasses } from "./bgClasses";
import {
  STANDARD_DENSITY,
  HERO_DENSITY,
  STANDARD_WIDTH,
  NARROW_WIDTH,
  OVERLAY_CLASSES,
  normalizeBgPosition,
  type GlobalSectionProps,
} from "./globalProps";
import { useRenderContext } from "./RenderContext";

interface SectionShellProps {
  globals: GlobalSectionProps;
  children: ReactNode;
  /** true = no container div; children manage their own container */
  bare?: boolean;
  /** Which density scale to use (default "standard") */
  densityScale?: "standard" | "hero";
  /** Which width scale to use (default "standard") */
  widthScale?: "standard" | "narrow";
  /** Extra classes on the outer <section> */
  className?: string;
  /** Extra classes on the inner container div (ignored when bare) */
  containerClassName?: string;
  /** Inline styles on the outer <section> */
  style?: CSSProperties;
}

export function SectionShell({
  globals,
  children,
  bare = false,
  densityScale = "standard",
  widthScale = "standard",
  className = "",
  containerClassName = "",
  style,
}: SectionShellProps) {
  const { isEditor } = useRenderContext();
  const densityMap = densityScale === "hero" ? HERO_DENSITY : STANDARD_DENSITY;
  const widthMap = widthScale === "narrow" ? NARROW_WIDTH : STANDARD_WIDTH;

  const densityClass = densityMap[globals.density];
  const widthClass = widthMap[globals.width];
  const bg = bgClasses[globals.backgroundStyle] || bgClasses.surface;

  // Full-height mode: fill the viewport (Hero-only in practice).
  // Canvas approximation: 100svh in the editor canvas would measure the
  // portal viewport, not the device viewport being previewed. We substitute
  // a fixed 800px height as a deliberate approximation proportional to a
  // real desktop screen.
  const isFull = globals.density === "full";
  const fullHeightClass = isFull
    ? isEditor
      ? "min-h-[800px] flex flex-col justify-center"
      : "min-h-[100svh] flex flex-col justify-start pt-[20svh] @md:justify-center @md:pt-0"
    : "";

  const dividerTop = globals.topDivider ? "border-t border-current/10" : "";
  const dividerBottom = globals.bottomDivider ? "border-b border-current/10" : "";
  const mobileHide = globals.hideOnMobile ? "hidden @md:block" : "";

  // Background image handling (non-bare sections only; bare sections manage
  // their own bg-image rendering, e.g. Hero ImageLayout)
  const hasBgImage = !bare && !!globals.backgroundImageUrl;
  const bgImageStyle: CSSProperties | undefined = hasBgImage
    ? {
        backgroundImage: `url(${globals.backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: normalizeBgPosition(globals.backgroundPosition),
      }
    : undefined;
  const overlayClass =
    hasBgImage && globals.overlayOpacity !== "none"
      ? OVERLAY_CLASSES[globals.overlayOpacity] || OVERLAY_CLASSES.none
      : null;

  const sectionClasses = [
    densityClass,
    bg,
    dividerTop,
    dividerBottom,
    mobileHide,
    hasBgImage ? "relative" : "",
    fullHeightClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const mergedStyle = bgImageStyle ? { ...bgImageStyle, ...style } : style;

  if (bare) {
    return (
      <section className={sectionClasses} style={mergedStyle}>
        {children}
      </section>
    );
  }

  // In full-height mode the section is a flex column, and the container's
  // mx-auto overrides cross-axis stretch — force full width so short
  // content doesn't shrink-wrap the container and float it to center.
  const containerClasses = [
    isFull ? "w-full" : "",
    widthClass,
    hasBgImage ? "relative" : "",
    containerClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={sectionClasses} style={mergedStyle}>
      {overlayClass && <div className={`absolute inset-0 ${overlayClass}`} />}
      <div className={containerClasses}>{children}</div>
    </section>
  );
}
